import React, { useRef, useState, useMemo, useEffect } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ReferenceLine, XAxis,
} from 'recharts';
import { useSpring, useMotionValueEvent, useReducedMotion } from 'motion/react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  ChartConfig, ChartContainer, ChartTooltip,
} from '@/components/ui/chart';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface ChartDataPoint {
  name: string;
  value: number;
}

const formatGBP = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);

const formatCompact = (n: number) =>
  new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

// Custom tooltip that reads the data point's name from payload directly,
// bypassing the shadcn nameKey lookup bug where item.name (= dataKey)
// shadows payload.name (= actual data field).
function DataTooltip({ active, payload, formatValue }: {
  active?: boolean;
  payload?: Array<{ value: number; payload: Record<string, unknown> }>;
  formatValue?: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const fmt = formatValue ?? ((v: number) => v.toLocaleString());
  return (
    <div className="border-border/50 bg-background rounded-[var(--radius-control)] border px-2.5 py-1.5 text-xs shadow-[var(--shadow-overlay)]">
      <div className="grid gap-1">
        {payload.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={{ background: (item.payload.fill as string) || 'var(--chart-1)' }}
              />
              <span className="text-muted-foreground">{item.payload.name as string}</span>
            </div>
            <span className="font-mono font-medium tabular-nums text-foreground">
              {fmt(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Revenue Card — Clipped Area Chart
// ---------------------------------------------------------------------------

const revenueConfig = {
  value: { label: 'Revenue', color: 'var(--chart-1)' },
} satisfies ChartConfig;

interface RevenueChartProps {
  data: ChartDataPoint[];
  total: number;
  onClick?: () => void;
}

export function RevenueChart({ data, total, onClick }: RevenueChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [axis, setAxis] = useState(0);

  const prefersReducedMotion = useReducedMotion();
  const springX = useSpring(0, { damping: 30, stiffness: 100 });
  const springY = useSpring(0, { damping: 30, stiffness: 100 });

  useMotionValueEvent(springX, 'change', (latest) => setAxis(latest));

  // Initialise spring to chart width on mount
  useEffect(() => {
    const w = chartRef.current?.getBoundingClientRect().width;
    if (w) springX.jump(w);
  }, []);

  return (
    <Card
      className={`py-4 gap-3 h-full shadow-none rounded-[var(--radius-panel)] transition-colors ${onClick ? 'cursor-pointer hover:border-primary/30 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]' : ''}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardHeader className="px-4 pb-0 gap-1">
        <CardDescription className="text-xs font-medium text-muted-foreground">Revenue</CardDescription>
        <CardTitle className="flex items-center gap-2 text-xl tabular-nums">
          {formatGBP(total)}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 pb-0 mt-auto">
        <ChartContainer ref={chartRef} className="h-28 w-full" config={revenueConfig}>
          <AreaChart
            className="overflow-visible"
            accessibilityLayer
            data={data}
            onMouseMove={(state) => {
              const x = state.activeCoordinate?.x;
              const v = state.activePayload?.[0]?.value;
              if (x !== undefined && v !== undefined) {
                if (prefersReducedMotion) {
                  springX.jump(x);
                  springY.jump(v as number);
                } else {
                  springX.set(x);
                  springY.set(v as number);
                }
              }
            }}
            onMouseLeave={() => {
              const w = chartRef.current?.getBoundingClientRect().width || 0;
              const finalV = data.length > 0 ? data[data.length - 1].value : 0;
              if (prefersReducedMotion) {
                springX.jump(w);
                springY.jump(finalV);
              } else {
                springX.set(w);
                if (data.length > 0) springY.jump(finalV);
              }
            }}
            margin={{ right: 0, left: 0, top: 4, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              horizontalCoordinatesGenerator={({ height }) => [0, height - 20]}
            />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              tick={{ fontSize: 10 }}
            />
            <ChartTooltip cursor={false} content={<DataTooltip formatValue={formatGBP} />} />
            <Area
              dataKey="value"
              type="monotone"
              fill="url(#revenue-gradient)"
              fillOpacity={0.2}
              stroke="var(--color-value)"
              clipPath={`inset(0 ${
                Number(chartRef.current?.getBoundingClientRect().width) - axis
              } 0 0)`}
            />
            <line
              x1={axis}
              y1={0}
              x2={axis}
              y2="85%"
              stroke="var(--color-value)"
              strokeDasharray="3 3"
              strokeLinecap="round"
              strokeOpacity={0.2}
            />
            {/* Ghost line behind clipped area */}
            <Area
              dataKey="value"
              type="monotone"
              fill="none"
              stroke="var(--color-value)"
              strokeOpacity={0.1}
            />
            <defs>
              <linearGradient id="revenue-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
              </linearGradient>
            </defs>
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. Orders Card — Value Line Bar Chart
// ---------------------------------------------------------------------------

const ordersConfig = {
  value: { label: 'Orders', color: 'var(--chart-2)' },
} satisfies ChartConfig;

const CHART_MARGIN = 30;

interface OrdersChartProps {
  data: ChartDataPoint[];
  total: number;
  onClick?: () => void;
}

function OrdersRefLabel({ viewBox, value }: { viewBox?: { x?: number; y?: number }; value: number }) {
  const x = viewBox?.x ?? 0;
  const y = viewBox?.y ?? 0;
  const w = value.toString().length * 7 + 10;
  return (
    <>
      <rect x={x - CHART_MARGIN} y={y - 8} width={w} height={16} fill="var(--chart-2)" rx={3} />
      <text fontWeight={600} x={x - CHART_MARGIN + 5} y={y + 4} fill="var(--primary-foreground)" fontSize={10}>
        {value}
      </text>
    </>
  );
}

export function OrdersChart({ data, total, onClick }: OrdersChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const highlighted = useMemo(() => {
    if (activeIndex !== undefined) return { index: activeIndex, value: data[activeIndex]?.value ?? 0 };
    return data.reduce(
      (max, d, i) => (d.value > max.value ? { index: i, value: d.value } : max),
      { index: 0, value: 0 },
    );
  }, [activeIndex, data]);

  const prefersReducedMotion = useReducedMotion();
  const springVal = useSpring(highlighted.value, { stiffness: 100, damping: 20 });
  const [springy, setSpringy] = useState(highlighted.value);

  useMotionValueEvent(springVal, 'change', (v) => setSpringy(Number(v.toFixed(0))));
  useEffect(() => {
    if (prefersReducedMotion) {
      springVal.jump(highlighted.value);
    } else {
      springVal.set(highlighted.value);
    }
  }, [highlighted.value, prefersReducedMotion]);

  return (
    <Card
      className={`py-4 gap-3 h-full shadow-none rounded-[var(--radius-panel)] transition-colors ${onClick ? 'cursor-pointer hover:border-primary/30 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]' : ''}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardHeader className="px-4 pb-0 gap-1">
        <CardDescription className="text-xs font-medium text-muted-foreground">Orders</CardDescription>
        <CardTitle className="flex items-center gap-2 text-xl tabular-nums">
          {total.toLocaleString('en-GB')}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 pb-0 mt-auto">
        <ChartContainer config={ordersConfig} className="h-28 w-full">
          <BarChart
            accessibilityLayer
            data={data}
            onMouseLeave={() => setActiveIndex(undefined)}
            margin={{ left: CHART_MARGIN, right: 4, top: 4, bottom: 0 }}
          >
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              tick={{ fontSize: 10 }}
            />
            <ChartTooltip cursor={{ fill: 'var(--muted)', opacity: 0.4 }} content={<DataTooltip />} />
            <Bar dataKey="value" fill="var(--color-value)" radius={3}>
              {data.map((_, i) => (
                <Cell
                  key={i}
                  className="duration-200"
                  opacity={i === highlighted.index ? 1 : 0.2}
                  onMouseEnter={() => setActiveIndex(i)}
                />
              ))}
            </Bar>
            <ReferenceLine
              opacity={0.4}
              y={springy}
              stroke="var(--chart-2)"
              strokeWidth={1}
              strokeDasharray="3 3"
              label={<OrdersRefLabel value={highlighted.value} />}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 3. Stock Card — Ranked Horizontal Comparison
// ---------------------------------------------------------------------------

const STOCK_COLORS = ['var(--data-categorical-1)', 'var(--data-categorical-2)', 'var(--data-categorical-3)'] as const;

interface StockChartProps {
  data: ChartDataPoint[];
  total: number;
  onClick?: () => void;
}

export function StockChart({ data, total, onClick }: StockChartProps) {
  const chartData = useMemo(
    () =>
      [...data]
        .sort((a, b) => b.value - a.value)
        .map((d, i) => ({
          name: d.name,
          value: d.value,
          fill: STOCK_COLORS[i % STOCK_COLORS.length],
        })),
    [data],
  );

  const maxVal = Math.max(...chartData.map(d => d.value), 1);

  return (
    <Card
      className={`py-4 gap-3 h-full shadow-none rounded-[var(--radius-panel)] transition-colors ${onClick ? 'cursor-pointer hover:border-primary/30 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]' : ''}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardHeader className="px-4 pb-0 gap-1">
        <CardDescription className="text-xs font-medium text-muted-foreground">Inventory snapshot</CardDescription>
        <CardTitle className="text-xl tabular-nums">
          {total.toLocaleString('en-GB')}
          <span className="text-sm font-normal text-muted-foreground ml-1.5">units</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-2 mt-auto flex flex-col justify-center">
        <div
          className="flex flex-col gap-2.5 w-full mt-2 h-24 overflow-y-auto pr-1"
          role="list"
          aria-label="Stock distribution by brand"
        >
          <div className="sr-only">Stock ranking: {chartData.map(d => `${d.name} (${d.value})`).join(', ')}</div>
          {chartData.map((d) => (
            <div key={d.name} role="listitem" className="flex flex-col gap-1.5 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground truncate mr-2 text-xs">{d.name}</span>
                <span className="font-medium text-foreground tabular-nums shrink-0 text-xs">{d.value.toLocaleString()}</span>
              </div>
              <div className="h-1.5 w-full bg-secondary overflow-hidden rounded-full">
                <div className="h-full rounded-full transition-[width] duration-[var(--motion-standard)] ease-[var(--ease-out-standard)] motion-reduce:transition-none" style={{ width: `${(d.value / maxVal) * 100}%`, backgroundColor: d.fill }} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 4. Top Agent Card — Ranked Horizontal Comparison
// ---------------------------------------------------------------------------

interface AgentChartProps {
  data: ChartDataPoint[];
  agentName: string;
  orderCount: number;
  revenue: number;
  onClick?: () => void;
}

export function AgentChart({ data, agentName, orderCount, revenue, onClick }: AgentChartProps) {
  const formatGBPShort = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', notation: 'compact', maximumFractionDigits: 1 }).format(n);

  const sortedData = useMemo(() => [...data].sort((a, b) => b.value - a.value), [data]);
  const maxVal = Math.max(...sortedData.map(d => d.value), 1);

  return (
    <Card
      className={`py-4 gap-3 h-full shadow-none rounded-[var(--radius-panel)] transition-colors ${onClick ? 'cursor-pointer hover:border-primary/30 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]' : ''}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardHeader className="px-4 pb-0 gap-1">
        <CardDescription className="text-xs font-medium text-muted-foreground">Top agent</CardDescription>
        <CardTitle className="text-xl">{agentName}</CardTitle>
        <CardDescription className="text-xs tabular-nums text-muted-foreground">
          {orderCount} orders &middot; {formatGBPShort(revenue)}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-2 mt-auto flex flex-col justify-center">
        <div
          className="flex flex-col gap-2.5 w-full mt-2 h-24 overflow-y-auto pr-1"
          role="list"
          aria-label="Top agent performance leaderboard"
        >
          <div className="sr-only">Agent ranking: {sortedData.map(d => `${d.name} (${d.value})`).join(', ')}</div>
          {sortedData.map((d) => (
            <div key={d.name} role="listitem" className="flex flex-col gap-1.5 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground truncate mr-2 text-xs">{d.name}</span>
                <span className="font-medium text-foreground tabular-nums shrink-0 text-xs">{d.value.toLocaleString()}</span>
              </div>
              <div className="h-1.5 w-full bg-secondary overflow-hidden rounded-full">
                <div className="h-full rounded-full transition-[width] duration-[var(--motion-standard)] ease-[var(--ease-out-standard)] motion-reduce:transition-none" style={{ width: `${(d.value / maxVal) * 100}%`, backgroundColor: 'var(--data-primary)' }} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
